import { Button, FormLabel, Typography } from "@mui/material";
import isEqual from "lodash/isEqual";
import omitBy from "lodash/omitBy";
import { FieldLabel } from "src/components/ui/FieldLabel";
import { OrgScopedBaseUser, User } from "src/stores/models/user";
import {
  isQualityEventApproval,
  QualityEventAnswers,
  QualityEventApprovalDecision,
  QualityEventDraft,
  QualityEventField,
  QualityEventRecord,
  qualityEventRows,
  QualityEventStep,
  QualityEventValue,
} from "src/types/quality-events";
import { cn } from "src/utils/ui";
import { getUserDisplayName } from "src/utils/user";
import {
  getIncompleteQualityEventFieldPaths,
  getQualityEventFieldRequirement,
  isQualityEventFieldRequired,
  isQualityEventFieldVisible,
} from "./quality-event-conditions";
import { getQualityEventDraft } from "./quality-event-draft";
import { QualityEventApprovalControls } from "./QualityEventApprovalControls";
import { QualityEventFieldValue } from "./QualityEventFieldValue";
import { QualityEventValueInput } from "./QualityEventValueInput";

type QualityEventStepEdit = {
  draft: QualityEventDraft;
  onChange?: (draft: QualityEventDraft) => void;
};

type QualityEventStepRenderContext = {
  orgId: string;
  step: QualityEventStep;
  record?: QualityEventRecord;
  files: { id: string; filename: string }[];
  users: OrgScopedBaseUser[];
  userId: string;
  currentUser?: User;
  pending: boolean;
  onApprovalDecision?: (
    fieldKey: string,
    decision: QualityEventApprovalDecision
  ) => Promise<void>;
  edit?: QualityEventStepEdit;
  allAnswers: QualityEventAnswers;
  showOutstanding: boolean;
  disabled: boolean;
  dirty: boolean;
  approveDisabled: boolean;
  updateDraft: (changes: Partial<QualityEventDraft>) => void;
  invalidFieldPaths: Set<string>;
};

type QualityEventFieldGridProps = {
  fields: QualityEventField[];
  values: QualityEventAnswers;
  context: QualityEventStepRenderContext;
  fieldPathPrefix?: string;
  completionRequirements: QualityEventStep["completionRequirements"];
  onFieldChange: (key: string, value: unknown) => void;
};

const QualityEventRepeatableField = ({
  field,
  value,
  context,
  required,
  outstanding,
  invalid,
  onChange,
}: {
  field: Extract<QualityEventField, { type: "repeatable" }>;
  value: unknown;
  context: QualityEventStepRenderContext;
  required: boolean;
  outstanding?: string;
  invalid: boolean;
  onChange: (value: unknown) => void;
}) => {
  const rows = qualityEventRows(value);
  return (
    <>
      {context.edit && (
        <div className="flex items-center justify-between gap-3">
          <FormLabel error={invalid} aria-invalid={invalid || undefined}>
            <FieldLabel
              edit
              label={field.label}
              required={required}
              outstanding={outstanding}
            />
          </FormLabel>
          <Button
            size="small"
            disabled={context.disabled}
            aria-label={`Add ${field.label}`}
            onClick={() =>
              onChange([...rows, { id: crypto.randomUUID(), answers: {} }])
            }
          >
            Create action
          </Button>
        </div>
      )}
      {rows.map((row, index) => (
        <div
          key={row.id}
          className={
            context.edit
              ? "flex flex-col gap-3 rounded bg-color-surface px-[10.5px] py-[7px] [container-type:inline-size]"
              : "rounded bg-color-surface px-[10.5px] py-[7px] [container-type:inline-size]"
          }
        >
          <QualityEventFieldGrid
            fields={field.fields}
            values={row.answers}
            context={context}
            fieldPathPrefix={`${field.key}:${row.id}:`}
            completionRequirements={field.completionRequirements}
            onFieldChange={(key, nextValue) =>
              onChange(
                rows.map((item) =>
                  item.id === row.id
                    ? {
                        ...item,
                        answers: { ...item.answers, [key]: nextValue },
                      }
                    : item
                )
              )
            }
          />
          {context.edit && (
            <Button
              color="error"
              size="small"
              disabled={context.disabled}
              aria-label={`Remove ${field.label} ${index + 1}`}
              onClick={() => {
                const answers = {
                  ...context.edit!.draft.answers,
                  [field.key]: rows.filter((item) => item.id !== row.id),
                };
                context.updateDraft({
                  answers,
                  files: omitBy(context.edit!.draft.files, (_, key) =>
                    key.startsWith(`${field.key}:${row.id}:`)
                  ),
                });
              }}
            >
              Remove
            </Button>
          )}
        </div>
      ))}
      {!context.edit && !rows.length && (
        <Typography variant="body1">No items recorded</Typography>
      )}
    </>
  );
};

const QualityEventStepField = ({
  field,
  value,
  scope,
  fieldPath,
  context,
  completionRequirements,
  onChange,
}: {
  field: QualityEventField;
  value: unknown;
  scope: QualityEventAnswers;
  fieldPath: string;
  context: QualityEventStepRenderContext;
  completionRequirements: QualityEventStep["completionRequirements"];
  onChange: (value: unknown) => void;
}) => {
  const fieldRequirement = getQualityEventFieldRequirement(
    field,
    field.type === "file" && context.edit?.draft.files[fieldPath]?.length
      ? context.edit.draft.files[fieldPath]
      : value,
    scope,
    completionRequirements
  );
  const outstanding = context.showOutstanding ? fieldRequirement : undefined;
  const required = isQualityEventFieldRequired(field, scope);
  const invalid = context.invalidFieldPaths.has(fieldPath);

  if (field.type === "approval") {
    if (!context.record) return null;
    const approval = context.record.steps[context.step.key]?.answers[field.key];
    return (
      <div className="col-span-full min-w-0">
        <QualityEventApprovalControls
          key={
            isQualityEventApproval(approval) ? approval.requestId : field.key
          }
          record={context.record}
          stepKey={context.step.key}
          field={field}
          value={value}
          users={context.users}
          userId={context.userId}
          currentUser={context.currentUser}
          disabled={context.pending}
          readOnly={!context.edit?.onChange}
          dirty={context.dirty}
          approveDisabled={context.approveDisabled}
          showOutstanding={context.showOutstanding}
          required={required}
          onChange={context.edit && onChange}
          onDecision={
            context.onApprovalDecision &&
            ((decision) => context.onApprovalDecision!(field.key, decision))
          }
        />
      </div>
    );
  }

  const wide = ["rich_text", "repeatable", "file"].includes(field.type);
  const fieldClassName = cn(
    context.edit ? "flex min-w-0 flex-col gap-3" : "min-w-0",
    wide && "col-span-full"
  );
  const content =
    field.type === "repeatable" ? (
      <QualityEventRepeatableField
        field={field}
        value={value}
        context={context}
        required={required}
        outstanding={outstanding}
        invalid={invalid}
        onChange={onChange}
      />
    ) : context.edit ? (
      <QualityEventValueInput
        field={field}
        value={value as QualityEventValue | undefined}
        users={context.users}
        disabled={context.disabled}
        readOnly={!context.edit.onChange}
        required={required}
        outstanding={outstanding}
        invalid={invalid}
        files={context.edit.draft.files[fieldPath] ?? []}
        existingFiles={context.files}
        onFiles={(files) =>
          context.updateDraft({
            files: { ...context.edit!.draft.files, [fieldPath]: files },
          })
        }
        onChange={onChange}
      />
    ) : (
      <QualityEventFieldValue
        field={field}
        value={value as QualityEventValue | undefined}
        files={context.files}
        orgId={context.orgId}
        users={context.users}
      />
    );

  if (context.edit) return <div className={fieldClassName}>{content}</div>;

  return (
    <div className={fieldClassName}>
      <Typography component="dt" variant="body2">
        <FieldLabel label={field.label} outstanding={outstanding} />
      </Typography>
      <dd className="m-0 mt-1 [&_.wmde-markdown]:text-inherit">{content}</dd>
    </div>
  );
};

const QualityEventFieldGrid = ({
  fields,
  values,
  context,
  fieldPathPrefix = "",
  completionRequirements,
  onFieldChange,
}: QualityEventFieldGridProps) => {
  const scope = { ...context.allAnswers, ...values };
  const visibleFields = fields.filter((field) =>
    isQualityEventFieldVisible(field, scope)
  );
  const Container = context.edit ? "div" : "dl";
  const containerClassName = cn(
    "grid grid-cols-1 items-start gap-[16px] [@container(min-width:640px)]:grid-cols-2",
    !context.edit && "m-0"
  );
  return (
    <Container className={containerClassName}>
      {visibleFields.map((field) => (
        <QualityEventStepField
          key={field.key}
          field={field}
          value={values[field.key]}
          scope={scope}
          fieldPath={`${fieldPathPrefix}${field.key}`}
          context={context}
          completionRequirements={completionRequirements}
          onChange={(value) => onFieldChange(field.key, value)}
        />
      ))}
    </Container>
  );
};

export const QualityEventStepValues = ({
  orgId,
  step,
  record,
  answers: suppliedAnswers,
  workflowAnswers,
  files,
  users,
  userId = "",
  currentUser,
  pending = false,
  onApprovalDecision,
  edit,
  hasUnsavedChanges = false,
  invalidRequirementPaths = [],
}: {
  orgId: string;
  step: QualityEventStep;
  record?: QualityEventRecord;
  answers?: QualityEventAnswers;
  workflowAnswers?: QualityEventAnswers;
  files: { id: string; filename: string }[];
  users: OrgScopedBaseUser[];
  userId?: string;
  currentUser?: User;
  pending?: boolean;
  hasUnsavedChanges?: boolean;
  onApprovalDecision?: (
    fieldKey: string,
    decision: QualityEventApprovalDecision
  ) => Promise<void>;
  edit?: QualityEventStepEdit;
  invalidRequirementPaths?: string[];
}) => {
  const savedStep = record?.steps[step.key];
  const answers =
    edit?.draft.answers ?? suppliedAnswers ?? savedStep?.answers ?? {};
  const allAnswers = {
    ...Object.assign(
      {},
      ...Object.values(record?.steps ?? {}).map(
        (savedStep) => savedStep.answers
      )
    ),
    ...workflowAnswers,
    ...answers,
  };
  const showOutstanding = !!(
    record &&
    record.status !== "CLOSED" &&
    record.currentStepKey === step.key &&
    savedStep?.updatedAt
  );
  const updateDraft = (changes: Partial<QualityEventDraft>) => {
    if (edit?.onChange) edit.onChange({ ...edit.draft, ...changes });
  };
  const disabled = pending || !edit?.onChange;
  const dirty =
    hasUnsavedChanges ||
    !!(
      onApprovalDecision &&
      record &&
      edit &&
      (!isEqual(answers, getQualityEventDraft({ record, step }).answers) ||
        edit.draft.title !== record.title ||
        edit.draft.owner !== savedStep?.owner ||
        edit.draft.dueDate !== savedStep?.dueDate ||
        Object.values(edit.draft.files).some((draftFiles) => draftFiles.length))
    );
  const invalidFieldPaths = new Set(invalidRequirementPaths);
  const approveDisabled =
    getIncompleteQualityEventFieldPaths({
      fields: step.fields,
      values: answers,
      allAnswers,
      completionRequirements: step.completionRequirements,
      files: edit?.draft.files ?? {},
      requiredFieldPaths: new Set(),
      validateAllRequirements: true,
    }).size > 0;
  const context: QualityEventStepRenderContext = {
    orgId,
    step,
    record,
    files,
    users,
    userId,
    currentUser,
    pending,
    onApprovalDecision,
    edit,
    allAnswers,
    showOutstanding,
    disabled,
    dirty,
    approveDisabled,
    updateDraft,
    invalidFieldPaths,
  };
  const responsibleUser = savedStep?.owner
    ? users.find((user) => user.id === savedStep.owner)
    : record?.createdBy;
  const responsibleUserName = getUserDisplayName(responsibleUser ?? {});

  return (
    <div className="flex flex-col gap-4 [container-type:inline-size]">
      {!edit && record && (
        <dl className="m-0 grid grid-cols-1 gap-4 [@container(min-width:640px)]:grid-cols-2">
          <div>
            <Typography component="dt" variant="body2">
              Responsible person
            </Typography>
            <Typography component="dd" variant="body1" className="m-0 mt-1">
              {responsibleUserName}
            </Typography>
          </div>
          {savedStep?.dueDate && (
            <div>
              <Typography component="dt" variant="body2">
                Due date
              </Typography>
              <Typography component="dd" variant="body1" className="m-0 mt-1">
                {savedStep.dueDate}
              </Typography>
            </div>
          )}
        </dl>
      )}
      <QualityEventFieldGrid
        fields={step.fields}
        values={answers}
        context={context}
        completionRequirements={step.completionRequirements}
        onFieldChange={(key, value) =>
          updateDraft({ answers: { ...answers, [key]: value } })
        }
      />
    </div>
  );
};
